import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';


interface AdminActivity {
  timestamp: Date;
  user: string;
  action: string;
  status: 'Success' | 'Failed';
}

interface DashboardCard {
  title: string;
  value: number | string;
  icon: string;
  color: string;
  route?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

interface RecentActivity {
  id: number;
  type: string;
  description: string;
  timestamp: Date;
  user: string;
  status: 'completed' | 'pending' | 'failed';
}

interface QuickAction {
  title: string;
  description: string;
  icon: string;
  route: string;
  color: string;
}

@Component({
  selector: 'app-registrardashboard',
  templateUrl: './registrardashboard.component.html',
  styleUrls: ['./registrardashboard.component.scss']
})
export class RegistrardashboardComponent implements OnInit {
  
  currentUser: any;
  isLoading = false;
  currentAcademicYear = '2025/2026';
  currentSemester = 'First Semester';
  currentTime: Date = new Date();



  // Dashboard Statistics
  dashboardCards: DashboardCard[] = [
    {
      title: 'Total Students',
      value: 0,
      icon: 'fa-users',
      color: 'primary',
      route: '/students',
      trend: { value: 12, isPositive: true }
    },
    {
      title: 'New Applications',
      value: 0,
      icon: 'fa-file-alt',
      color: 'success',
      route: '/applications',
      trend: { value: 8, isPositive: true }
    },
    {
      title: 'Pending Registrations',
      value: 43,
      icon: 'fa-clock',
      color: 'warning',
      route: '/registrations',
      trend: { value: 5, isPositive: false }
    },
    {
      title: 'Active Programmes',
      value: 18,
      icon: 'fa-graduation-cap',
      color: 'info',
      route: '/programmes'
    }
  ];

  // Quick Actions
  quickActions: QuickAction[] = [
    {
      title: 'Register Student',
      description: 'Register new or returning students',
      icon: 'fa-user-plus',
      route: '/register-student',
      color: 'primary'
    },
    {
      title: 'View Applications',
      description: 'Review pending applications',
      icon: 'fa-folder-open',
      route: '/applications',
      color: 'success'
    },
    {
      title: 'Generate Reports',
      description: 'Create enrollment and academic reports',
      icon: 'fa-chart-bar',
      route: '/reports',
      color: 'info'
    },
    {
      title: 'Manage Transcripts',
      description: 'Process transcript requests',
      icon: 'fa-file-invoice',
      route: '/transcripts',
      color: 'warning'
    },
    {
      title: 'Academic Calendar',
      description: 'View and manage academic calendar',
      icon: 'fa-calendar-alt',
      route: '/calendar',
      color: 'danger'
    },
    {
      title: 'Student Records',
      description: 'Access and update student records',
      icon: 'fa-database',
      route: '/student-records',
      color: 'secondary'
    }
  ];

  // Recent Activities
  recentActivities: RecentActivity[] = [
    {
      id: 1,
      type: 'Registration',
      description: 'Student registration completed for John Mensah',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      user: 'Admin User',
      status: 'completed'
    },
    {
      id: 2,
      type: 'Application',
      description: 'New application submitted by Sarah Adjei',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
      user: 'System',
      status: 'pending'
    },
    {
      id: 3,
      type: 'Transcript',
      description: 'Transcript request approved for Michael Boateng',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000),
      user: 'Registrar Office',
      status: 'completed'
    },
    {
      id: 4,
      type: 'Report',
      description: 'Monthly enrollment report generated',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
      user: 'System',
      status: 'completed'
    },
    {
      id: 5,
      type: 'Update',
      description: 'Student record updated for Grace Owusu',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      user: 'Admin User',
      status: 'completed'
    }
  ];

  // Upcoming Events
  upcomingEvents = [
    {
      title: 'Registration Deadline',
      date: new Date('2025-10-15'),
      type: 'deadline',
      description: 'Last day for course registration'
    },
    {
      title: 'Semester Exams',
      date: new Date('2025-11-20'),
      type: 'exam',
      description: 'First semester examinations begin'
    },
    {
      title: 'Graduation Ceremony',
      date: new Date('2025-12-10'),
      type: 'event',
      description: 'Annual graduation ceremony'
    }
  ];

  // Chart Data (placeholder)
  enrollmentData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    values: [120, 145, 160, 155, 170, 185]
  };

  constructor(
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadUserData();
    this.loadDashboardData();
  }

  loadUserData(): void {
    // Get user from localStorage or set default
    const userStr = localStorage.getItem('sysadmin_user');
    this.currentUser = userStr ? JSON.parse(userStr) : { username: 'Registrar' };
  }

  loadDashboardData(): void {
    this.isLoading = true;
    // Simulate API call
    setTimeout(() => {
      this.isLoading = false;
    }, 1000);
  }

  navigateTo(route: string): void {
    if (route) {
      this.router.navigate([route]);
    }
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'completed': 'status-completed',
      'pending': 'status-pending',
      'failed': 'status-failed'
    };
    return statusClasses[status] || '';
  }

  getStatusIcon(status: string): string {
    const statusIcons: { [key: string]: string } = {
      'completed': 'fa-check-circle',
      'pending': 'fa-clock',
      'failed': 'fa-times-circle'
    };
    return statusIcons[status] || 'fa-circle';
  }

  getActivityIcon(type: string): string {
    const activityIcons: { [key: string]: string } = {
      'Registration': 'fa-user-plus',
      'Application': 'fa-file-alt',
      'Transcript': 'fa-file-invoice',
      'Report': 'fa-chart-bar',
      'Update': 'fa-edit'
    };
    return activityIcons[type] || 'fa-info-circle';
  }

  getEventIcon(type: string): string {
    const eventIcons: { [key: string]: string } = {
      'deadline': 'fa-calendar-times',
      'exam': 'fa-pencil-alt',
      'event': 'fa-calendar-check'
    };
    return eventIcons[type] || 'fa-calendar';
  }

  formatTimeAgo(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  getDaysUntil(date: Date): number {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  refreshDashboard(): void {
    this.loadDashboardData();
  }

  logout(): void {
    localStorage.removeItem('sysadmin_token');
    localStorage.removeItem('sysadmin_user');
    this.router.navigate(['/super-login']);
  }
}